/**
 * Searchable picker family — taxonomy, countries, currencies, 20+ rows.
 * Rules: .tondocs/ui/DROPDOWNS.md · tokens: lib/dropdown-styles.ts
 */
"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMobile } from "@/hooks/use-mobile";
import { DockSheet } from "@/components/ui/dock-sheet";
import { SearchablePickerList } from "@/components/ui/searchable-picker-list";
import { SHEET_Z_PICKER } from "@/lib/sheet-styles";
import {
  SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS,
  SEARCHABLE_DROPDOWN_TRIGGER_BASE,
  searchableDropdownPanelClass,
  searchableDropdownTriggerClass,
} from "@/lib/dropdown-styles";

import type { SearchableOption } from "@/lib/dropdown-types";

export type Option = SearchableOption;

interface SearchableDropdownProps {
  options: Option[];
  label: string;
  selectedValue: Option | null;
  onSelect: (option: Option) => void;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  searchInputClassName?: string;
  optionClassName?: string;
  placeholder?: string;
  showCode?: boolean;
  showFlag?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loading?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  sheetTitle?: string;
  onSearchQueryChange?: (query: string) => void;
  searchDebounceMs?: number;
  mobileSheet?: boolean;
  closeOnSelect?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  label,
  selectedValue,
  onSelect,
  className,
  triggerClassName,
  contentClassName,
  searchInputClassName,
  optionClassName,
  placeholder = "Select...",
  showCode = false,
  showFlag = true,
  searchPlaceholder = "Search...",
  emptyMessage = "No results found",
  loading = false,
  onOpenChange,
  sheetTitle,
  onSearchQueryChange,
  searchDebounceMs = 300,
  mobileSheet = true,
  closeOnSelect = true,
}) => {
  const isMobile = useMobile();
  const useMobileSheet = mobileSheet && isMobile;

  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (useMobileSheet) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        onOpenChange?.(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onOpenChange, useMobileSheet]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
    setQuery("");
    onSearchQueryChange?.("");
  }, [onOpenChange, onSearchQueryChange]);

  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (!onSearchQueryChange) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSearchQueryChange(value);
      }, searchDebounceMs);
    },
    [onSearchQueryChange, searchDebounceMs],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSelect = (option: Option) => {
    setQuery("");
    onSearchQueryChange?.("");
    onSelect(option);
    if (closeOnSelect) {
      closeDropdown();
    }
  };

  const openDropdown = () => {
    setIsOpen(true);
    onOpenChange?.(true);
  };

  const toggleDropdown = () => {
    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  };

  const trigger = (
    <div
      className={cn(
        triggerClassName,
        !triggerClassName && [SEARCHABLE_DROPDOWN_TRIGGER_BASE, searchableDropdownTriggerClass()],
      )}
      onClick={toggleDropdown}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleDropdown();
        }
      }}
    >
      <div className="flex-1 overflow-hidden">
        {selectedValue ? (
          <div className="flex items-center gap-2">
            {showFlag && selectedValue.flag ? (
              selectedValue.flag.startsWith("http") ? (
                <img src={selectedValue.flag} alt="" className="w-5 h-5 shrink-0" />
              ) : (
                <span className="text-lg">{selectedValue.flag}</span>
              )
            ) : null}
            <span className="truncate">
              {selectedValue.name} {showCode && selectedValue.code ? `(${selectedValue.code})` : ""}
            </span>
          </div>
        ) : (
          <span className={SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS}>{placeholder}</span>
        )}
      </div>
      <ChevronDown
        className={cn(
          "h-4 w-4 transition-transform duration-200 shrink-0",
          SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS,
          isOpen ? "rotate-180" : "",
        )}
      />
    </div>
  );

  const pickerProps = {
    options,
    label,
    query,
    onQueryChange: handleQueryChange,
    onSelect: handleSelect,
    showFlag,
    showCode,
    loading,
    emptyMessage,
    searchPlaceholder,
    optionClassName,
    searchInputClassName,
    clientFilter: !onSearchQueryChange,
    selectedCode: selectedValue?.code ?? null,
  };

  if (useMobileSheet) {
    return (
      <div className={cn("relative w-full", className)} ref={inputRef}>
        {trigger}
        <DockSheet
          open={isOpen}
          onOpenChange={(open) => {
            if (open) {
              setIsOpen(true);
              onOpenChange?.(true);
            } else {
              closeDropdown();
            }
          }}
          title={sheetTitle ?? placeholder}
          zIndex={SHEET_Z_PICKER}
          scrollManagedByChild
          contentClassName="flex min-h-0 flex-col overflow-hidden pt-0"
        >
          <div className="-mx-4 flex min-h-0 flex-1 flex-col">
            <SearchablePickerList {...pickerProps} variant="sheet" />
          </div>
        </DockSheet>
      </div>
    );
  }

  return (
    <div className={cn("relative w-full", className)} ref={inputRef}>
      {trigger}

      <div
        className={cn(
          "absolute z-30 top-full mt-2 w-full overflow-hidden shadow-2xl transition-all duration-300 ease-in-out",
          contentClassName || searchableDropdownPanelClass(),
          isOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 -translate-y-4 invisible",
        )}
        style={{ maxHeight: "min(240px, 40vh)" }}
      >
        <SearchablePickerList
          {...pickerProps}
          autoFocusSearch={isOpen}
          listStyle={{ maxHeight: "min(200px, 36vh)" }}
        />
      </div>
    </div>
  );
};

export default SearchableDropdown;
